import {
  JadeAuthAcceptInvite,
  JadeAuthForgotPassword,
  JadeAuthMfaSetup,
  JadeAuthMfaVerify,
  JadeAuthRecovery,
  JadeAuthResetPassword,
  parseResetQuery,
} from '@akaal/jade-ui'
import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'

import { helpRoutes } from '@/help/helpRoutes'
import { i18n } from '@/i18n'
import { useAuthStore } from '@/stores/auth'
import { useBrandStore } from '@/stores/brand'
import { useEnterpriseStore } from '@/stores/enterprise'
import { resolveNavigation, type RouteMeta as ConsoleRouteMeta } from './guards'

declare module 'vue-router' {
  interface RouteMeta extends ConsoleRouteMeta {
    /** Catalogue key for the page title and the shell heading. */
    titleKey?: string
  }
}

// Every console view is lazy: the sign-in page should not carry the console
// with it. The pre-console pages are the library's own components, already in
// the main chunk, wired through the AuthFlow context App.vue provides.
const routes: RouteRecordRaw[] = [
  {
    path: '/sign-in',
    name: 'sign-in',
    component: () => import('@/views/SignInView.vue'),
    meta: { public: true, titleKey: 'signIn.title' },
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: JadeAuthForgotPassword,
    meta: { public: true, titleKey: 'password.forgotTitle' },
  },
  {
    // The API emails a link to this path with token and email in the query.
    path: '/reset-password',
    name: 'reset-password',
    component: JadeAuthResetPassword,
    props: (route) => ({ link: parseResetQuery(route.query) }),
    meta: { public: true, titleKey: 'password.resetTitle' },
  },
  {
    // The API emails an invitation link to this path with token and email in the query.
    path: '/accept-invite',
    name: 'accept-invite',
    component: JadeAuthAcceptInvite,
    props: (route) => ({ link: parseResetQuery(route.query) }),
    meta: { public: true, titleKey: 'invite.title' },
  },
  {
    // The identity provider returns the browser here with `code` and `state`
    // in the query. A handoff, like impersonation: it must open whatever this
    // browser holds, since a stale half-finished sign-in would otherwise
    // bounce it to the MFA page before the code is ever posted.
    path: '/auth/sso/callback',
    name: 'sso-callback',
    component: () => import('@/views/SsoCallbackView.vue'),
    meta: { handoff: true, titleKey: 'ssoCallback.title' },
  },
  {
    // The operator panel opens this page and posts an impersonation token to it.
    path: '/impersonate',
    name: 'impersonate',
    component: () => import('@/views/ImpersonateView.vue'),
    meta: { handoff: true, titleKey: 'impersonation.handoffTitle' },
  },
  {
    // Google's setup wizard opens this on a device enrolling by sign-in URL,
    // with a provisioningInfo query parameter. A handoff like the two above:
    // the WebView holds no session, or a stale one, and neither may divert it.
    path: '/enrol/sign-in/:config',
    name: 'device-sign-in',
    component: () => import('@/views/enrolment/DeviceSignInView.vue'),
    meta: { handoff: true, titleKey: 'deviceSignIn.title' },
  },
  {
    path: '/mfa/recovery',
    name: 'mfa-recovery',
    component: JadeAuthRecovery,
    meta: { stage: 'mfa', titleKey: 'mfa.recoverySignInTitle' },
  },
  {
    path: '/mfa/verify',
    name: 'mfa-verify',
    component: JadeAuthMfaVerify,
    meta: { stage: 'mfa', titleKey: 'mfa.verifyTitle' },
  },
  {
    path: '/mfa/setup',
    name: 'mfa-setup',
    component: JadeAuthMfaSetup,
    meta: { stage: 'mfa', titleKey: 'mfa.setupTitle' },
  },
  {
    path: '/',
    component: () => import('@/layouts/ConsoleLayout.vue'),
    children: [
      {
        path: '',
        name: 'dashboard',
        component: () => import('@/views/DashboardView.vue'),
        meta: { titleKey: 'dashboard.title' },
      },
      {
        path: 'devices',
        name: 'devices',
        component: () => import('@/views/DevicesView.vue'),
        meta: {
          titleKey: 'devices.title',
          permission: 'devices.read',
          // Devices only exist once Google has an enterprise for this tenant.
          requiresEnterprise: true,
        },
      },
      {
        // Fleet › Retired (F6). Before `devices/:id`, so "retired" is never read as an id.
        path: 'devices/retired',
        name: 'devices-retired',
        component: () => import('@/views/RetiredDevicesView.vue'),
        meta: {
          titleKey: 'retire.listTitle',
          permission: 'devices.read',
          requiresEnterprise: true,
        },
      },
      {
        // Fleet › Map (F1). Before `devices/:id`, so "map" is never read as an id.
        path: 'devices/map',
        name: 'devices-map',
        component: () => import('@/views/FleetMapView.vue'),
        meta: {
          titleKey: 'map.title',
          permission: 'devices.read',
          requiresEnterprise: true,
        },
      },
      {
        // The results of a bulk command (F4), kept apart from `devices/:id`.
        path: 'devices/commands/:batch',
        name: 'command-batch',
        component: () => import('@/views/CommandBatchView.vue'),
        meta: {
          titleKey: 'bulk.resultsTitle',
          permission: 'devices.read',
          requiresEnterprise: true,
        },
      },
      {
        // The viewer of a view-only remote session (F2, D138).
        path: 'devices/:id/remote',
        name: 'device-remote',
        component: () => import('@/views/RemoteView.vue'),
        meta: {
          titleKey: 'remote.title',
          permission: 'devices.remote_view',
          requiresEnterprise: true,
        },
      },
      {
        path: 'devices/:id',
        name: 'device',
        component: () => import('@/views/DeviceView.vue'),
        meta: {
          titleKey: 'devices.detailTitle',
          permission: 'devices.read',
          requiresEnterprise: true,
        },
      },
      {
        path: 'profiles',
        name: 'profiles',
        component: () => import('@/views/ProfilesView.vue'),
        meta: { titleKey: 'profiles.title', permission: 'profiles.read' },
      },
      {
        path: 'profiles/new',
        name: 'profile-new',
        component: () => import('@/views/ProfileView.vue'),
        meta: { titleKey: 'profiles.newTitle', permission: 'profiles.write' },
      },
      {
        path: 'profiles/:id',
        name: 'profile',
        component: () => import('@/views/ProfileView.vue'),
        meta: { titleKey: 'profiles.detailTitle', permission: 'profiles.read' },
      },
      {
        path: 'policies',
        name: 'policies',
        component: () => import('@/views/PoliciesView.vue'),
        meta: { titleKey: 'policies.title', permission: 'profiles.read' },
      },
      {
        path: 'policies/new',
        name: 'policy-new',
        component: () => import('@/views/PolicyView.vue'),
        meta: { titleKey: 'policies.newTitle', permission: 'policies.write' },
      },
      {
        path: 'policies/reorder',
        name: 'policy-reorder',
        component: () => import('@/views/PolicyReorderView.vue'),
        meta: { titleKey: 'policies.reorderTitle', permission: 'policies.write' },
      },
      {
        path: 'policies/:id',
        name: 'policy',
        component: () => import('@/views/PolicyView.vue'),
        meta: { titleKey: 'policies.detailTitle', permission: 'profiles.read' },
      },
      {
        path: 'apps',
        name: 'apps',
        component: () => import('@/views/AppsView.vue'),
        meta: {
          titleKey: 'apps.title',
          permission: 'profiles.read',
          // The Play iframe is Google's; without a bound enterprise there is no token to open it with.
          requiresEnterprise: true,
        },
      },
      {
        path: 'managed-users',
        name: 'managed-users',
        component: () => import('@/views/ManagedUsersView.vue'),
        meta: { titleKey: 'managedUsers.title', permission: 'managed_users.read' },
      },
      {
        path: 'reports',
        name: 'reports',
        component: () => import('@/views/ReportsView.vue'),
        meta: {
          titleKey: 'reports.title',
          permission: 'devices.read',
          // Every report reads the fleet, which only exists once Google has an enterprise.
          requiresEnterprise: true,
        },
      },
      {
        path: 'tags',
        name: 'tags',
        component: () => import('@/views/TagsView.vue'),
        meta: { titleKey: 'tags.title', permission: 'managed_users.read' },
      },
      {
        path: 'enrolment',
        name: 'enrolment',
        component: () => import('@/views/EnrolmentView.vue'),
        meta: {
          titleKey: 'enrolment.title',
          permission: 'enrolment.manage',
          // Tokens are cut against a Google enterprise; there is nothing to do before one exists.
          requiresEnterprise: true,
        },
      },
      {
        path: 'enterprise/onboarding',
        name: 'enterprise-onboarding',
        component: () => import('@/views/EnterpriseOnboardingView.vue'),
        meta: { titleKey: 'enterprise.onboardingTitle' },
      },
      {
        // Google returns the admin's browser to the API, which redirects here
        // with the outcome. Inside the shell because the session is still live.
        path: 'enterprise/callback',
        name: 'enterprise-callback',
        component: () => import('@/views/EnterpriseCallbackView.vue'),
        meta: { titleKey: 'enterprise.callbackTitle' },
      },
      {
        path: 'settings',
        component: () => import('@/views/SettingsView.vue'),
        redirect: { name: 'settings-users' },
        children: [
          {
            path: 'users',
            name: 'settings-users',
            component: () => import('@/views/settings/UsersPanel.vue'),
            meta: { titleKey: 'consoleUsers.title', permission: 'users.read' },
          },
          {
            // Preferences are the caller's own; a session is all the API asks for.
            path: 'notifications',
            name: 'settings-notifications',
            component: () => import('@/views/settings/NotificationsPanel.vue'),
            meta: { titleKey: 'notifications.title' },
          },
          {
            path: 'audit',
            name: 'settings-audit',
            component: () => import('@/views/settings/AuditPanel.vue'),
            meta: { titleKey: 'audit.title', permission: 'audit.read' },
          },
          {
            path: 'api-keys',
            name: 'settings-api-keys',
            component: () => import('@/views/settings/ApiKeysPanel.vue'),
            meta: { titleKey: 'apiKeys.title', permission: 'api_keys.manage' },
          },
          {
            path: 'webhooks',
            name: 'settings-webhooks',
            component: () => import('@/views/settings/WebhooksPanel.vue'),
            meta: { titleKey: 'webhooks.title', permission: 'webhooks.manage' },
          },
          {
            // The definitions are read with devices.read, but this tab only edits them.
            path: 'custom-attributes',
            name: 'settings-custom-attributes',
            component: () => import('@/views/settings/CustomAttributesPanel.vue'),
            meta: { titleKey: 'customAttributes.title', permission: 'devices.manage' },
          },
          {
            path: 'sso',
            name: 'settings-sso',
            component: () => import('@/views/settings/SsoPanel.vue'),
            meta: { titleKey: 'sso.title', permission: 'sso.manage' },
          },
          {
            // Only a tenant the operator white-labelled has a brand to edit
            // (#222); the tab hides otherwise and the panel explains a 404.
            path: 'branding',
            name: 'settings-branding',
            component: () => import('@/views/settings/BrandingPanel.vue'),
            meta: { titleKey: 'branding.title', permission: 'brand.manage' },
          },
        ],
      },
      {
        path: 'billing',
        component: () => import('@/views/BillingView.vue'),
        redirect: { name: 'billing-overview' },
        children: [
          {
            path: '',
            name: 'billing-overview',
            component: () => import('@/views/billing/OverviewPanel.vue'),
            meta: { titleKey: 'billing.title', permission: 'billing.read' },
          },
          {
            path: 'payment-method',
            name: 'billing-payment-method',
            component: () => import('@/views/billing/PaymentMethodPanel.vue'),
            meta: { titleKey: 'billing.paymentMethod.title', permission: 'billing.read' },
          },
          {
            path: 'invoices',
            name: 'billing-invoices',
            component: () => import('@/views/billing/InvoicesPanel.vue'),
            meta: { titleKey: 'billing.invoices.title', permission: 'billing.read' },
          },
        ],
      },
      {
        path: 'billing/invoices/:id',
        name: 'billing-invoice',
        component: () => import('@/views/InvoiceView.vue'),
        meta: { titleKey: 'billing.invoice.title', permission: 'billing.read' },
      },
      {
        // Any tenant user may see the organisation leaving; who may confirm it is the page's business.
        path: 'offboarding',
        name: 'offboarding',
        component: () => import('@/views/OffboardingView.vue'),
        meta: { titleKey: 'offboarding.title' },
      },
      {
        path: 'settings/enterprise',
        name: 'enterprise-settings',
        component: () => import('@/views/EnterpriseSettingsView.vue'),
        meta: { titleKey: 'enterprise.settingsTitle', permission: 'devices.read' },
      },
      {
        // The signed-in person's own account: every role has one, so no permission.
        path: 'account',
        name: 'account',
        component: () => import('@/views/AccountView.vue'),
        meta: { titleKey: 'account.title' },
      },
      ...helpRoutes,
      {
        path: 'forbidden',
        name: 'forbidden',
        component: () => import('@/views/ForbiddenView.vue'),
        meta: { titleKey: 'error.forbiddenTitle' },
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
    meta: { public: true, titleKey: 'error.notFoundTitle' },
  },
]

export const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior: (_to, _from, saved) => saved ?? { top: 0 },
})

router.beforeEach(async (to) => {
  const auth = useAuthStore()
  // One re-validation per page load, before the first route resolves, so no
  // guard ever decides on a session it has not checked.
  if (!auth.ready) await auth.restore()

  const enterprise = useEnterpriseStore()
  // Only fetched for the pages that care. The store answers from cache after
  // the first read, and stays silent without a session, for operators and for
  // anyone without permission to read a binding.
  if (to.meta.requiresEnterprise === true) await enterprise.load()

  return resolveNavigation(
    { name: String(to.name ?? ''), fullPath: to.fullPath, meta: to.meta },
    {
      isAuthenticated: auth.isAuthenticated,
      mfaStage: auth.mfaStage,
      // A page opens on the permission the session holds; whether a write is
      // possible right now (a read-only tenant) is the page's own business.
      can: auth.has,
      enterpriseBound: enterprise.bound,
    },
  )
})

router.afterEach((to) => {
  const key = to.meta.titleKey
  if (typeof key !== 'string') return
  // Tab title only. The brand half is MDM42 until `/auth/me` names a
  // white-label tenant's brand, and the store re-titles the page then.
  useBrandStore().setPageTitle(i18n.global.t(key))
})
